// src/components/settings/CashRemarkCategoryDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, EyeOff, Eye } from 'lucide-react';
import { cashRemarkCategoriesApi } from '../../api';

const CashRemarkCategoryDialog = ({ onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null); // null=不在编辑，'new'=新增，数字=编辑对应id
  const [form, setForm] = useState({ name: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadCategories(); }, []);

  // 这个管理弹窗，停用的分类也要能看到（方便重新启用），不像结账时的下拉框
  // 只查 active 状态那些
  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await cashRemarkCategoriesApi.getList();
      if (res.success) setCategories(res.data || []);
    } catch (err) {
      console.error('加载现金备注分类失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId('new');
    setForm({ name: '', sort_order: 0 });
    setError('');
  };

  const openEdit = (cat) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, sort_order: cat.sort_order || 0 });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('分类名称不能为空'); return; }
    setSaving(true);
    setError('');
    try {
      let res;
      if (editingId === 'new') {
        res = await cashRemarkCategoriesApi.create(form);
      } else {
        res = await cashRemarkCategoriesApi.update(editingId, form);
      }
      if (res.success) {
        setEditingId(null);
        loadCategories();
      } else {
        setError(res.message || '保存失败');
      }
    } catch (err) {
      setError(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 停用/重新启用——不做物理删除，避免已经被历史订单引用过的分类出现悬空数据
  const toggleStatus = async (cat) => {
    try {
      if (cat.status === 'active') {
        await cashRemarkCategoriesApi.deactivate(cat.id);
      } else {
        await cashRemarkCategoriesApi.update(cat.id, { status: 'active' });
      }
      loadCategories();
    } catch (err) {
      alert(err.message || '操作失败');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="p-6 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="font-bold text-lg">现金收款备注分类</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-3">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-8">加载中...</p>
          ) : categories.length === 0 && editingId !== 'new' ? (
            <p className="text-sm text-gray-400 text-center py-8">还没有任何分类，点下面"新增分类"添加</p>
          ) : (
            categories.map(cat => (
              editingId === cat.id ? (
                <div key={cat.id} className="border rounded-lg p-3 space-y-2 bg-blue-50">
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-1.5 border rounded-lg text-sm" placeholder="分类名称" autoFocus />
                  {error && <p className="text-xs text-red-500">{error}</p>}
                  <div className="flex gap-2">
                    <button onClick={cancelEdit} className="flex-1 px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">取消</button>
                    <button onClick={handleSave} disabled={saving}
                      className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                      {saving ? '保存中...' : '保存'}
                    </button>
                  </div>
                </div>
              ) : (
                <div key={cat.id} className={`flex items-center justify-between border rounded-lg p-3 ${cat.status !== 'active' ? 'opacity-50' : ''}`}>
                  <span className="text-sm text-gray-800">
                    {cat.name}
                    {cat.status !== 'active' && <span className="text-xs text-gray-400 ml-2">（已停用）</span>}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(cat)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => toggleStatus(cat)} className="p-1.5 text-gray-400 hover:text-amber-600 rounded"
                      title={cat.status === 'active' ? '停用' : '重新启用'}>
                      {cat.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )
            ))
          )}

          {editingId === 'new' && (
            <div className="border rounded-lg p-3 space-y-2 bg-blue-50">
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-1.5 border rounded-lg text-sm" placeholder="分类名称，如：找零备用金" autoFocus />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <div className="flex gap-2">
                <button onClick={cancelEdit} className="flex-1 px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50">取消</button>
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t flex-shrink-0">
          {editingId === null && (
            <button onClick={openNew}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              <Plus className="w-4 h-4" />新增分类
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashRemarkCategoryDialog;